import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ProjectModal.css';
import apiUtils, { getAuthHeaders, getVectorToolsByIds } from '../utils/api';
import YumlConfigFields from './YumlConfigFields';
import ProjectToolsEditorModal from './ProjectToolsEditorModal';
import { normalizePromptToolsList } from '../utils/promptTools';

// Note: Live name validation removed per requirements to hide the name check during editing.

/**
 * ProjectModal - reusable for create and edit
 * props:
 * - mode: 'create' | 'edit'
 * - initial: { project_id?, name, description }
 * - onClose()
 * - onSaved(project)
 */
const ProjectModal = ({ mode = 'create', initial = { name: '', description: '', opening_description: '' }, onClose, onSaved }) => {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [openingDescription, setOpeningDescription] = useState(initial?.opening_description || '');
  const [isPublic, setIsPublic] = useState(initial?.is_public !== false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Hide nameStatus UI per requirement; only show an error banner on submit if taken

  // Agent Settings state (Prompt)
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [promptExists, setPromptExists] = useState(false);
  const [systemMessage, setSystemMessage] = useState('');
  // Platform removed per requirement

  /** Catalog prompts (page_location project_chat_catalog) — definitions live on Prompts page */
  const [catalogPrompts, setCatalogPrompts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  /** Ordered prompt ids linked to this project for chat workflow buttons */
  const [linkedPromptIds, setLinkedPromptIds] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [buttonsError, setButtonsError] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [projectSuffix, setProjectSuffix] = useState('rsmg_sales');
  /** null = unrestricted (all horizon tools); string[] = explicit allowlist */
  const [projectTools, setProjectTools] = useState(null);
  /** When true, chat skips platform helpers (account web search, email draft, etc.) */
  const [horizonOnlyTools, setHorizonOnlyTools] = useState(false);
  const [projectToolDetails, setProjectToolDetails] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsEditorOpen, setToolsEditorOpen] = useState(false);
  const [yumlConfig, setYumlConfig] = useState(
    initial?.yuml_config && typeof initial.yuml_config === 'object'
      ? initial.yuml_config
      : { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_agent_reply' }
  );

  const isEdit = mode === 'edit';
  const isArchived = !!(initial?.archived_at);

  useEffect(() => {
    setName(initial?.name || '');
    setDescription(initial?.description || '');
    setOpeningDescription(initial?.opening_description || '');
    setIsPublic(initial?.is_public !== false);
    if (initial?.suffix) setProjectSuffix(initial.suffix);
    setYumlConfig(
      initial?.yuml_config && typeof initial.yuml_config === 'object'
        ? initial.yuml_config
        : { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_agent_reply' }
    );
  }, [initial]);

  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${apiUtils.API_BASE_URL}/casc/v1/api/data-settings/active-suffix`, { headers });
        const json = await resp.json();
        if (json?.currentSuffix || json?.suffix) setProjectSuffix(json.currentSuffix || json.suffix);
      } catch (_) { /* keep default */ }
    })();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // No local dropdown logic here; encapsulated in ModelSelect

  const title = isEdit ? 'Edit Project' : 'New Project';
  const canSubmit = useMemo(() => {
    const trimmed = (name || '').trim();
    return !!trimmed;
  }, [name]);
  const inFlightRef = useRef(false);

  const onSubmit = async (e) => {
    e && e.preventDefault();
    if (inFlightRef.current) {
      return; // already processing a submit — avoid duplicate payloads
    }
    setError('');
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    // Preflight validation for name uniqueness (only on submit, banner on failure)
    setSubmitting(true);
    inFlightRef.current = true;
    try {
      const trimmedName = trimmed;
      if (!isEdit || trimmedName !== (initial?.name || '').trim()) {
        // Validate name via API
        try {
          const headers = await getAuthHeaders();
          const vresp = await fetch(`${apiUtils.API_BASE_URL}/casc/v1/api/chat/project/validate-name`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ name: trimmedName })
          });
          const vjson = await vresp.json().catch(() => ({}));
          const exists = !!(vjson?.data && vjson.data.exists);
          const valid = !!(vjson?.data && vjson.data.valid);
          if (!valid || exists) {
            setError(exists ? 'That project name is already taken.' : 'Project name is invalid.');
            setSubmitting(false);
            return;
          }
        } catch (_) { /* If validation fails silently, rely on server error below */ }
      }
      let url = `${apiUtils.API_BASE_URL}/casc/v1/api/chat/project/create`;
      let body = {
        name: trimmed,
        description: description || '',
        opening_description: openingDescription || '',
        is_public: isPublic,
      };
      if (isEdit) {
        url = `${apiUtils.API_BASE_URL}/casc/v1/api/chat/project/modify`;
        body = {
          projectId: initial?.project_id,
          name: trimmed,
          description,
          opening_description: openingDescription || '',
          is_public: isPublic,
          yuml_config: yumlConfig,
        };
      }
      const headers = await getAuthHeaders();
      const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) {
        const msg = json?.message || json?.error || resp.statusText || 'Request failed';
        throw new Error(msg);
      }
      const savedProject = json.data;

      // Upsert the Agent Settings prompt whenever we have a saved project id (create or edit)
      if (savedProject?.project_id) {
        try {
          const promptId = savedProject.project_id;
          // Determine if the prompt already exists. On some backends, the project create
          // endpoint auto-creates a prompt with the same id. In that case, we must update
          // instead of create to avoid a 400 "already exists" error.
          let exists = false;
          try {
            const pr = await apiUtils.getPromptById(promptId);
            exists = !!(pr && pr.prompt && !pr.notFound);
          } catch (_) {
            exists = false;
          }

          const upsertPayload = {
            name: savedProject.name, // mirror
            description: savedProject.description || '', // mirror
            category: 'project',
            prompt: '',
            system_message: systemMessage || '',
            temperature: 1.0,
            model: null,
            response_format: null,
            page_location: null,
            // null = all tools on this horizon; array = explicit project allowlist
            tools: projectTools,
            tool_policy: horizonOnlyTools ? 'horizon_only' : 'inherit',
          };
          if (promptExists || exists) {
            await apiUtils.updatePrompt(promptId, upsertPayload);
            setPromptExists(true);
          } else {
            try {
              await apiUtils.createPrompt({ id: promptId, ...upsertPayload });
              setPromptExists(true);
            } catch (ce) {
              const m = (ce?.message || '').toLowerCase();
              // Fallback to update if backend reports the prompt already exists
              if (m.includes('already exists') || m.includes('exists')) {
                await apiUtils.updatePrompt(promptId, upsertPayload);
                setPromptExists(true);
              } else {
                throw ce;
              }
            }
          }
          setAgentError('');
        } catch (e2) {
          // Show error and stop close
          setAgentError(e2.message || 'Failed to save Agent Settings');
          throw e2; // propagate to keep modal open
        }
        try {
          await apiUtils.setProjectPromptButtons(savedProject.project_id, linkedPromptIds);
          setButtonsError('');
        } catch (e3) {
          setButtonsError(e3.message || 'Failed to save workflow buttons');
          throw e3;
        }
      }

      onSaved && onSaved(savedProject);
    } catch (err) {
      // Normalize name-taken message if server returns conflict
      const msg = (err?.message || '').toLowerCase();
      if (msg.includes('exists') || msg.includes('duplicate') || msg.includes('taken')) {
        setError('That project name is already taken.');
      } else {
        setError(err.message || 'Failed to save project');
      }
    } finally {
      setSubmitting(false);
      inFlightRef.current = false;
    }
  };

  // Load Agent Settings (prompt) when editing existing project
  useEffect(() => {
    const loadPrompt = async () => {
      if (!isEdit || !initial?.project_id) return;
      setAgentLoading(true);
      setAgentError('');
      try {
        const res = await apiUtils.getPromptById(initial.project_id);
        if (res?.notFound) {
          setPromptExists(false);
          setSystemMessage('');
          setProjectTools(null);
          setHorizonOnlyTools(false);
        } else if (res?.prompt) {
          const pr = res.prompt;
          setPromptExists(true);
          setSystemMessage(pr.system_message || '');
          const policy = String(pr.tool_policy || '').trim().toLowerCase();
          setHorizonOnlyTools(policy === 'horizon_only' || policy === 'horizon-only' || policy === 'horizon');
          if (pr.tools == null) {
            setProjectTools(null);
          } else {
            setProjectTools(normalizePromptToolsList(pr.tools));
          }
        }
      } catch (e) {
        setAgentError(e.message || 'Failed to load Agent Settings');
      } finally {
        setAgentLoading(false);
      }
    };
    loadPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initial?.project_id]);

  // Resolve labels for selected project tools
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (projectTools == null) {
        setProjectToolDetails([]);
        return;
      }
      if (!projectTools.length) {
        setProjectToolDetails([]);
        return;
      }
      setToolsLoading(true);
      try {
        const items = await getVectorToolsByIds(projectTools, undefined, initial?.suffix || projectSuffix);
        const map = new Map((items || []).map((t) => [String(t.id), t]));
        if (!cancelled) {
          setProjectToolDetails(projectTools.map((id) => {
            const t = map.get(String(id));
            return t
              ? { id, name: t.name || t.slug || id, kind: t.tool_kind || t.kind || 'search', missing: false }
              : { id, name: id, kind: 'unknown', missing: true };
          }));
        }
      } catch (_) {
        if (!cancelled) {
          setProjectToolDetails(projectTools.map((id) => ({ id, name: id, kind: 'unknown', missing: true })));
        }
      } finally {
        if (!cancelled) setToolsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectTools, projectSuffix, initial?.suffix]);

  // Load project chat button catalog (prompt definitions)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${apiUtils.API_BASE_URL}/casc/v1/api/prompts`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ page_location: 'project_chat_catalog' }),
        });
        const json = await resp.json().catch(() => ({}));
        if (cancelled || !resp.ok || !json?.success) {
          if (!cancelled) setCatalogPrompts([]);
          return;
        }
        const list = Array.isArray(json.prompts) ? json.prompts : [];
        if (!cancelled) {
          setCatalogPrompts(list.filter(p => p.page_location === 'project_chat_catalog' || p.page_location === 'project'));
        }
      } catch (_) {
        if (!cancelled) setCatalogPrompts([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load ordered workflow button ids when editing an existing project
  useEffect(() => {
    if (!isEdit || !initial?.project_id) {
      setLinkedPromptIds([]);
      setButtonsError('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLinksLoading(true);
      setButtonsError('');
      try {
        const prompts = await apiUtils.listProjectPromptButtons(initial.project_id);
        if (cancelled) return;
        const ids = prompts.map(p => p.id);
        setLinkedPromptIds(ids);
        setCatalogPrompts(prev => {
          const m = new Map((prev || []).map(p => [p.id, p]));
          prompts.forEach(p => m.set(p.id, p));
          return Array.from(m.values());
        });
      } catch (_) {
        if (!cancelled) setLinkedPromptIds([]);
      } finally {
        if (!cancelled) setLinksLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, initial?.project_id]);

  const moveLinked = (idx, delta) => {
    setLinkedPromptIds(arr => {
      const j = idx + delta;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      const t = next[idx];
      next[idx] = next[j];
      next[j] = t;
      return next;
    });
  };

  const removeLinked = (pid) => {
    setLinkedPromptIds(ids => ids.filter(x => x !== pid));
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-panel project-modal-panel" role="dialog" aria-modal="true" aria-labelledby="project-modal-title">
        <div className="modal-header">
          <h3 id="project-modal-title" style={{ margin: 0 }}>{title}</h3>
          <button className="btn ghost" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        {/* Use explicit submission via button onClick to avoid duplicate native form submits */}
        <form onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div className="modal-body">
            {error && (
              <div className="error-banner" role="alert">
                <i className="fas fa-exclamation-triangle" /> {error}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="project-name">Name</label>
              <input
                id="project-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // Prevent native form submit and route through guarded handler
                    e.preventDefault();
                    onSubmit(e);
                  }
                }}
                placeholder="Project name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="project-description">Description</label>
              <textarea id="project-description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the project" />
            </div>

            {isEdit && initial?.suffix && (
              <div className="form-group">
                <label>Horizon</label>
                <div className="section-card" style={{ padding: 12 }}>
                  <code>{initial.suffix}</code>
                  <p className="subtle" style={{ fontSize: '0.85em', margin: '8px 0 0' }}>
                    This project’s tools and data come from this horizon (Settings → Tools on this suffix).
                    That can differ from your current <strong>Settings → User → Active Horizon</strong>.
                  </p>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="project-opening-description">Opening message</label>
              <textarea
                id="project-opening-description"
                rows={5}
                value={openingDescription}
                onChange={(e) => setOpeningDescription(e.target.value)}
                placeholder="Here is the text to print to the user when they open a brand new chat…"
              />
              <div style={{ fontSize: '0.82em', color: '#888', marginTop: '4px' }}>
                Project load message on <strong>Start a new chat</strong> (and on headless <code>GET /projects</code>).
                Not sent to the model — use <strong>Agent system message</strong> for LLM instructions.
                Leave blank for the default hint.
              </div>
            </div>

            {/* Visibility toggle */}
            <div className="form-group">
              <label>Visibility</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                <button
                  type="button"
                  className={`btn btn-sm ${isPublic ? 'primary' : 'ghost'}`}
                  onClick={() => setIsPublic(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', fontSize: '0.9em',
                    ...(isPublic ? {} : { opacity: 0.6 })
                  }}
                >
                  <i className="fas fa-globe" /> Public
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${!isPublic ? 'primary' : 'ghost'}`}
                  onClick={() => setIsPublic(false)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', fontSize: '0.9em',
                    ...(!isPublic ? {} : { opacity: 0.6 })
                  }}
                >
                  <i className="fas fa-lock" /> Private
                </button>
              </div>
              <div style={{ fontSize: '0.82em', color: '#888', marginTop: '4px' }}>
                {isPublic
                  ? 'All users in this horizon can see this project.'
                  : 'Only you (the owner) can see this project.'}
              </div>
            </div>

            {isEdit && initial?.is_owner && initial?.project_id && (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Archive</label>
                <div className="section-card" style={{ padding: 12 }}>
                  <p className="subtle" style={{ fontSize: '0.85em', marginBottom: 10 }}>
                    {isArchived
                      ? 'This project is archived. Other viewers cannot open it until you restore it.'
                      : 'Archiving hides this project from others until you restore it. Your chats remain available to you.'}
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm ghost"
                    disabled={archiveBusy}
                    onClick={async () => {
                      const pid = initial.project_id;
                      const wantArchive = !isArchived;
                      if (!window.confirm(
                        wantArchive
                          ? 'Archive this project?'
                          : 'Restore this project from the archive?'
                      )) return;
                      setArchiveBusy(true);
                      try {
                        const headers = await getAuthHeaders();
                        const resp = await fetch(`${apiUtils.API_BASE_URL}/casc/v1/api/chat/project/archive`, {
                          method: 'POST',
                          headers,
                          body: JSON.stringify({ projectId: pid, archived: wantArchive }),
                        });
                        const json = await resp.json().catch(() => ({}));
                        if (!resp.ok || !json?.success) {
                          throw new Error(json?.message || json?.error || 'Request failed');
                        }
                        onSaved && onSaved(json.data);
                      } catch (e) {
                        setError(e.message || 'Archive request failed');
                      } finally {
                        setArchiveBusy(false);
                      }
                    }}
                  >
                    {archiveBusy ? (
                      <><i className="fas fa-spinner fa-pulse" /> Please wait…</>
                    ) : isArchived ? (
                      <><i className="fas fa-box-open" /> Restore from archive</>
                    ) : (
                      <><i className="fas fa-box-archive" /> Archive project</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {(
                <div style={{marginTop: 12}}>
                    <div className="section-title">Agent Settings</div>
                    <p className="subtle" style={{ fontSize: '0.85em', margin: '0 0 8px' }}>
                      Configure how the chat agent behaves in <strong>this project</strong>. This is not{' '}
                      <strong>Settings → System</strong> (instance-wide embedding model and API keys).
                    </p>
                    <div className="section-card">
                        {agentLoading ? (
                            <div className="subtle"><i className="fas fa-spinner fa-pulse"/> Loading agent settings…
                            </div>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label htmlFor="agent-system">Agent system message</label>
                                    <textarea id="agent-system" rows={4} value={systemMessage}
                                              onChange={(e) => setSystemMessage(e.target.value)}
                                              placeholder="Instructions for the project chat agent (LLM system prompt)"/>
                                </div>
                                <p className="subtle" style={{ fontSize: '0.85em', marginTop: 0 }}>
                                  This system message shapes agent replies in this project only. For deployment-wide options (embedding model, shared ingestion API keys), use <strong>Settings → System</strong>.
                                </p>
                                <p className="subtle" style={{ fontSize: '0.85em', marginTop: 0 }}>
                                  Chat uses your active model from Settings → User. Tools below limit what the agent may call in this project (including workflow buttons and tool_chain steps). With no custom list, the agent can use every tool on <strong>this project&apos;s horizon</strong> — not tools from other horizons.
                                </p>
                                <div className="form-group project-tools-horizon-only" style={{ marginTop: 12 }}>
                                  <label className="project-tools-checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={horizonOnlyTools}
                                      onChange={(e) => setHorizonOnlyTools(e.target.checked)}
                                    />
                                    <span>Only use horizon tools</span>
                                  </label>
                                  <p className="subtle" style={{ fontSize: '0.8em', margin: '4px 0 0 1.6rem' }}>
                                    When checked, platform helpers (account web search, email draft, locked record, embedding) are excluded even if no custom tool list is set.
                                  </p>
                                </div>
                                <div className="form-group" style={{ marginTop: 12 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                    <label style={{ margin: 0 }}>Project tools</label>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                      {projectTools != null && (
                                        <button
                                          type="button"
                                          className="btn btn-sm ghost"
                                          onClick={() => setProjectTools(null)}
                                          title="Clear allowlist and use every tool on this horizon"
                                        >
                                          Use all tools
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="btn btn-sm primary"
                                        onClick={() => setToolsEditorOpen(true)}
                                      >
                                        Edit tools
                                      </button>
                                    </div>
                                  </div>
                                  {toolsLoading ? (
                                    <div className="subtle" style={{ marginTop: 8 }}>
                                      <i className="fas fa-spinner fa-pulse" /> Loading tool list…
                                    </div>
                                  ) : projectTools == null ? (
                                    <div className="project-tools-summary" style={{ marginTop: 8 }}>
                                      <span className="subtle">
                                        {horizonOnlyTools
                                          ? 'Using all tools on this horizon (platform helpers off).'
                                          : 'Using all tools on this horizon (plus platform helpers when available).'}
                                      </span>
                                    </div>
                                  ) : projectTools.length === 0 ? (
                                    <div className="project-tools-summary" style={{ marginTop: 8 }}>
                                      <span className="subtle">No tools selected — chat cannot call tools in this project.</span>
                                    </div>
                                  ) : (
                                    <ul className="project-tools-summary-list">
                                      {projectToolDetails.map((t) => (
                                        <li key={t.id} className={t.missing ? 'is-missing' : ''}>
                                          <strong>{t.name}</strong>
                                          <code>{t.kind}</code>
                                          {t.missing ? <span className="subtle"> (missing)</span> : null}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                {agentError && <div className="error-text">{agentError}</div>}
                                <div className="form-group" style={{ marginTop: 12 }}>
                                  <label>Default YUML output</label>
                                  <YumlConfigFields
                                    suffix={projectSuffix}
                                    value={yumlConfig}
                                    onChange={setYumlConfig}
                                    emitOn="after_agent_reply"
                                  />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div style={{ marginTop: 12 }}>
              <div className="section-title">Project chat workflow buttons</div>
              <div className="section-card">
                <p className="subtle" style={{ fontSize: '0.85em', marginBottom: 10 }}>
                  Pick which catalog prompts appear next to Attach in this project&apos;s chats. Create and edit definitions under Prompts → Project chat button catalog.
                </p>
                {catalogLoading || linksLoading ? (
                  <div className="subtle"><i className="fas fa-spinner fa-pulse" /> Loading workflow buttons…</div>
                ) : (
                  <>
                    {linkedPromptIds.map((pid, idx) => {
                      const pr = catalogPrompts.find(x => x.id === pid);
                      const label = pr?.name || pid;
                      return (
                        <div
                          key={pid}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
                            {label}
                          </span>
                          <button type="button" className="btn btn-sm ghost" onClick={() => moveLinked(idx, -1)} disabled={idx === 0} title="Move up">
                            <i className="fas fa-arrow-up" />
                          </button>
                          <button type="button" className="btn btn-sm ghost" onClick={() => moveLinked(idx, 1)} disabled={idx >= linkedPromptIds.length - 1} title="Move down">
                            <i className="fas fa-arrow-down" />
                          </button>
                          <button type="button" className="btn btn-sm ghost" onClick={() => removeLinked(pid)} title="Remove">
                            <i className="fas fa-times" /> Remove
                          </button>
                        </div>
                      );
                    })}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="workflow-add-select">Add workflow button</label>
                      <select
                        id="workflow-add-select"
                        className="form-control"
                        defaultValue=""
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) {
                            setLinkedPromptIds(ids => (ids.includes(v) ? ids : [...ids, v]));
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">— Select catalog prompt —</option>
                        {catalogPrompts.filter(p => !linkedPromptIds.includes(p.id)).map(p => (
                          <option key={p.id} value={p.id}>{p.name || p.id}</option>
                        ))}
                      </select>
                    </div>
                    {buttonsError && <div className="error-text" style={{ marginTop: 8 }}>{buttonsError}</div>}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            {!isEdit && (
              <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            )}
            <button type="button" onClick={onSubmit} className="btn primary" disabled={submitting || !canSubmit}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
      {toolsEditorOpen && (
        <ProjectToolsEditorModal
          suffix={initial?.suffix || projectSuffix}
          initialSelectedIds={projectTools == null ? [] : projectTools}
          seedAllWhenEmpty={projectTools == null}
          horizonOnly={horizonOnlyTools}
          onCancel={() => setToolsEditorOpen(false)}
          onApply={async (ids) => {
            const next = Array.isArray(ids) ? ids : [];
            setProjectTools(next);
            setToolsEditorOpen(false);
            // Persist immediately when editing an existing project so "Use these tools" writes settings.
            if (isEdit && initial?.project_id) {
              try {
                const promptId = initial.project_id;
                let current = null;
                try {
                  const pr = await apiUtils.getPromptById(promptId);
                  if (pr && pr.prompt && !pr.notFound) current = pr.prompt;
                } catch (_) {}
                const upsertPayload = {
                  name: (current && current.name) || name.trim() || initial?.name || 'Project',
                  description: (current && current.description) || description || '',
                  category: 'project',
                  prompt: (current && current.prompt) || '',
                  system_message: (current && current.system_message) || systemMessage || '',
                  temperature: (current && current.temperature != null) ? current.temperature : 1.0,
                  model: (current && current.model) || null,
                  response_format: (current && current.response_format) || null,
                  page_location: (current && current.page_location) || null,
                  tools: next,
                  tool_policy: horizonOnlyTools ? 'horizon_only' : 'inherit',
                };
                if (current) {
                  await apiUtils.updatePrompt(promptId, upsertPayload);
                  setPromptExists(true);
                } else {
                  await apiUtils.createPrompt({ id: promptId, ...upsertPayload });
                  setPromptExists(true);
                }
                setAgentError('');
              } catch (e) {
                setAgentError(e.message || 'Failed to save project tools');
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectModal;
